import {
  countLabel,
  panel,
  panelAction,
  panelActionCss,
  panelCss,
  rowList,
  rowsCss,
} from '../armKit';
import { analyticsDashboardUrl } from '../destinations';
import { readerCountries, universityReach } from '../pageData';
import { viewedUser } from '../profileFacts';
import { INK } from '../surface';

import type { Row } from '../armKit';
import type { ArmContext, ArmSpec } from '../armRegistry';
import type { Country, ReaderCountries, UniversityReach } from '../pageData';

type Mounted = { body: HTMLElement; link: HTMLElement | null };

const TOP_COUNTRIES = 5;
const TITLE = 'Where your readers are';
const DASHBOARD_LABEL = 'See all reader locations';
const DASHBOARD_SOURCE = 'profile-liautotest-geography-dashboard';
const UNIS_CLASS = 'profile-liautotest-geography__unis';

// UNIS_CLASS is the one line this arm draws itself; everything else here is a
// recipe from armKit.
function armCss(scope: string): string {
  return `${panelCss(scope, { marginBottom: '0' })}${rowsCss(scope)}${panelActionCss(scope)}
${scope} .${UNIS_CLASS} {
  font-size: 13px;
  line-height: 1.5;
  color: ${INK};
  margin: 12px 0 0;
}
`;
}

function countriesLine(count: number): string {
  return `Your papers have been read in ${countLabel(count, 'country')}.`;
}

// uni_count is a rolling 28-day window from a different pipeline to the
// all-time country totals, so the two are never phrased as the same period.
function universitiesLine({ count, name }: UniversityReach): string {
  return (
    `Readers at ${countLabel(count, 'university')} in the last four weeks — ` +
    `${name} among them.`
  );
}

function countryDisplayName(name: string): string {
  return name.replace(/^the\s+/i, '');
}

function rowsFor(countries: Country[]): Row[] {
  return countries.slice(0, TOP_COUNTRIES).map((country, index) => ({
    rank: index + 1,
    title: countryDisplayName(country.name),
    url: null,
    meta: countLabel(country.hits, 'view') ?? undefined,
  }));
}

function dashboardLink(ctx: ArmContext): HTMLAnchorElement | null {
  const user = viewedUser();
  if (user?.page_name == null || user.page_name === '') return null;

  return panelAction(
    DASHBOARD_LABEL,
    analyticsDashboardUrl(user.page_name, 'countries', user.domain_name),
    () => ctx.track(DASHBOARD_SOURCE),
  );
}

function mount(
  ctx: ArmContext,
  countries: ReaderCountries | null,
): Mounted | null {
  const slot = ctx.mountSlot('columnTop');
  if (slot == null) return null;

  ctx.injectStyles(armCss(ctx.scope));

  const { root, body } = panel({
    title: TITLE,
    subtitle: countries == null ? undefined : countriesLine(countries.count),
  });

  if (countries != null && countries.top.length > 0) {
    body.appendChild(rowList(rowsFor(countries.top)));
  }

  const link = dashboardLink(ctx);
  if (link != null) body.appendChild(link);

  slot.appendChild(root);

  return { body, link };
}

export const readerGeography: ArmSpec = {
  onReady: async (ctx) => {
    const countriesPromise = readerCountries(ctx.viewedUserId);
    const universitiesPromise = universityReach(ctx.viewedUserId);

    // Mounted the moment the cheap half lands. The university figure sits
    // behind a 30-day cache that rebuilds off DynamoDB on a miss, and a
    // container waiting on that is worse than a panel that grows once.
    //
    // pageData answers a real empty aggregate as count: 0 rather than null, so
    // the extra check is what keeps that reading as no signal at all, exactly
    // like the null case, instead of as a panel with nothing in it.
    const countries = await countriesPromise;
    const geography =
      countries != null && countries.count > 0 ? countries : null;
    const mounted = geography == null ? null : mount(ctx, geography);

    const unis = await universitiesPromise;
    if (unis == null) return;

    const target = mounted ?? mount(ctx, null);
    if (target == null) return;

    const line = document.createElement('p');
    line.className = UNIS_CLASS;
    line.textContent = universitiesLine(unis);
    target.body.insertBefore(line, target.link);
  },
};
